import {captureException} from '@sentry/browser';
import {match} from 'ts-pattern';

import {isNotNil} from 'ramda';

type NormalizedWebsocketError = {
  error: Error;
  isTransient: boolean;
};

const sanitizeUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    return `${parsed.protocol}//${parsed.host}${parsed.pathname}`;
  } catch {
    const [base] = url.split('?');
    return base ?? 'unknown';
  }
};

const isWebSocketEvent = (value: unknown): value is {type: string; target?: unknown} =>
  typeof value === 'object' &&
  value !== null &&
  'type' in value &&
  typeof (value as {type: unknown}).type === 'string';

const isWebSocketTarget = (value: unknown): value is {url?: string; readyState?: number} =>
  typeof value === 'object' && value !== null;

const normalizeError = (error: unknown): NormalizedWebsocketError =>
  match(error)
    .when(
      (e): e is Error => e instanceof Error,
      (e) => ({error: e, isTransient: false})
    )
    .when(isWebSocketEvent, (e) => {
      const target = isWebSocketTarget(e.target) ? e.target : null;
      const rawUrl = target?.url;
      const url = isNotNil(rawUrl) ? sanitizeUrl(rawUrl) : 'unknown';
      const readyState = target?.readyState;
      return {
        error: new Error(`WebSocket ${e.type} event (url: ${url}, readyState: ${readyState})`),
        isTransient: true,
      };
    })
    .otherwise((e) => ({error: new Error(String(e)), isTransient: false}));

export const captureWebsocketError = (error: unknown) => {
  const {error: normalizedError, isTransient} = normalizeError(error);
  console.error('AppSync subscription error:', normalizedError.message);
  captureException(normalizedError, {
    level: isTransient ? 'warning' : 'error',
    tags: {
      module: 'appsync',
    },
  });
};
